// iter591 — изнесен от Dashboard.jsx (Днешен фокус: rule engine + streak + Pro награда)
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

export default function DailyFocusCard({ user, mealDilData, dilBudget, hiSummary, isDark, bg, API }) {
  const [showWhy, setShowWhy] = useState(false);
  const [backendFocus, setBackendFocus] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [streak, setStreak] = useState(null);
  const [proReward, setProReward] = useState(null); // {days_to_pro, granted, unlock_until}

  // iter578 — Fetch backend focus (rule engine + streak). Falls back to inline.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/dashboard/daily-focus`);
        if (!mounted) return;
        setBackendFocus(data);
        setCompleted(!!data?.completed_today);
        setStreak(data?.streak || null);
        setProReward(data?.pro_reward || null);
      } catch (e) {
        // silent — fall back to inline rule engine below
      }
    })();
    return () => { mounted = false; };
  }, [API]);

  const handleCompleted = async () => {
    if (completed) return;
    try {
      const { data } = await axios.post(`${API}/dashboard/focus-completed`, { rule: backendFocus?.rule || "unknown" });
      setCompleted(true);
      setStreak(data?.streak || null);
      if (data?.pro_reward_granted) {
        toast.success(bg
          ? "🎉 30 дни поредни! Получаваш безплатен Pro акаунт за 30 дни."
          : "🎉 30-day streak! You unlocked 30 days of free Pro.", { duration: 8000 });
        setProReward(prev => ({
          ...(prev || {}),
          granted: true,
          unlock_until: data.pro_unlock_until,
          days_to_pro: 0,
        }));
      }
    } catch {
      // Soft fallback — UI still shows "Записано"
      setCompleted(true);
    }
  };

  // Use backend payload if available, else inline rule engine
  const used = mealDilData?.daily_effective_dil ?? 0;
  const total = dilBudget || 97;
  const fraction = used / Math.max(total, 1);
  const goal = (user?.profile?.personal?.goal || "").toLowerCase();
  const noMealsToday = !mealDilData?.daily_effective_dil;
  const labs = hiSummary?.totals?.by_type?.lab_panel ?? 0;
  const meals30 = hiSummary?.metabolic?.meal_snapshots_count ?? 0;
  const profileWeight = !!user?.profile?.body?.weight;

  let focus = backendFocus;
  if (!focus) {
    if (noMealsToday) {
      focus = { action: bg ? "Запиши закуска с 25–30 g протеин." : "Log a breakfast with 25–30g protein.", effects: bg ? ["по-нисък глад до обяд", "по-стабилна кръвна захар", "по-добра ситост"] : ["less hunger until lunch", "stable blood sugar", "better satiety"], reasons: bg ? ["нисък прием на протеин в началото на деня", "по-добра инсулинова чувствителност след сън", goal.includes("отслабв") ? "цел: загуба на тегло" : "цел: метаболитен баланс"] : ["low morning protein", "post-sleep insulin sensitivity", "goal alignment"], cta_path: "/dashboard/nutrition-lab", cta_label: bg ? "Към хранителната лаборатория" : "To Nutrition Lab" };
    } else if (fraction > 0.85) {
      focus = { action: bg ? "Направи 30 минути ходене след обяд." : "Take a 30-min walk after lunch.", effects: bg ? ["намалена постпрандиална глюкоза", "по-нисък инсулин", "повече енергия"] : ["lower post-meal glucose", "lower insulin", "more energy"], reasons: bg ? ["днешният DIL е над 85% от лимита", "ходенето подобрява utilization на глюкоза", "ниско-интензивна интервенция = малък friction"] : ["DIL > 85% of cap", "walking aids glucose uptake", "low-friction intervention"], cta_path: "/dashboard/mindbody-lab", cta_label: bg ? "Към Mind & Body Lab" : "To Mind & Body Lab" };
    } else if (labs === 0) {
      focus = { action: bg ? "Качи последните си лабораторни изследвания." : "Upload your recent lab results.", effects: bg ? ["+12% точност на оценката", "персонализирани препоръки", "ранно откриване на риск"] : ["+12% model accuracy", "personalised recs", "early risk detection"], reasons: bg ? ["липсват лабораторни данни в модела", "точността на Health Intelligence ще се повиши", "възможност за ранно откриване на маркери"] : ["no labs in model", "boost HI accuracy", "early marker detection"], cta_path: "/dashboard/analyses", cta_label: bg ? "Към Precision Health" : "To Precision Health" };
    } else if (meals30 < 7) {
      focus = { action: bg ? "Запишете още 1 хранене за тази седмица." : "Log one more meal this week.", effects: bg ? ["по-точен метаболитен профил", "по-надеждни изводи", "по-смислени препоръки"] : ["sharper metabolic profile", "more reliable insights", "smarter recommendations"], reasons: bg ? ["под 7 записани хранения = слаб сигнал", "повече данни = по-точен персонален модел", "посоката не може да се определи"] : ["<7 meals = weak signal", "more data sharpens model", "trend undetermined"], cta_path: "/dashboard/nutrition-lab", cta_label: bg ? "Към хранителната лаборатория" : "To Nutrition Lab" };
    } else {
      focus = { action: goal.includes("отслабв") || goal.includes("weight") ? (bg ? "Опитай 12-часов prандиален прозорец днес (8:00–20:00)." : "Try a 12-hour eating window today.") : (bg ? "Включи 1 хапка ферментирала храна в основно ястие." : "Add one serving of fermented food today."), effects: bg ? ["по-добра инсулинова чувствителност", "разнообразен микробиом", "по-стабилна енергия"] : ["better insulin sensitivity", "diverse microbiome", "stable energy"], reasons: bg ? ["системата работи стабилно — време за оптимизация", profileWeight ? "теглото е въведено и моделът знае контекста" : "малки промени дават голям дългосрочен ефект", "този ход подпомага дългосрочна метаболитна устойчивост"] : ["system stable — optimize now", "context known", "long-term resilience"], cta_path: "/dashboard/nutrition-lab", cta_label: bg ? "Към хранителната лаборатория" : "To Nutrition Lab" };
    }
  }

  return (
    <section
      data-testid="cc-daily-focus"
      className={`rounded-2xl border p-4 ${
        isDark
          ? "bg-gradient-to-br from-emerald-900/20 via-[hsl(222,47%,9%)] to-cyan-900/10 border-emerald-700/30"
          : "bg-gradient-to-br from-emerald-50 via-white to-cyan-50 border-emerald-200"
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xl" aria-hidden="true">🎯</span>
          <h3 className={`text-sm font-bold uppercase tracking-wider ${isDark ? "text-emerald-200" : "text-emerald-800"}`}>
            {bg ? "Днешен фокус" : "Today's focus"}
          </h3>
        </div>
        {streak && streak.current > 0 && (
          <div className="flex items-center gap-1.5">
            <span
              data-testid="cc-daily-focus-streak"
              className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${
                isDark ? "bg-amber-500/20 text-amber-200" : "bg-amber-100 text-amber-800"
              }`}
              title={bg ? `Най-дълъг: ${streak.longest} дни` : `Longest: ${streak.longest} days`}
            >
              🔥 {streak.current} {bg ? (streak.current === 1 ? "ден" : "поредни дни") : (streak.current === 1 ? "day" : "in a row")}
            </span>
            {typeof streak.freezes_remaining === "number" && streak.freezes_remaining > 0 && (
              <span
                data-testid="cc-daily-focus-freeze"
                className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                  isDark ? "bg-sky-500/20 text-sky-200" : "bg-sky-100 text-sky-800"
                }`}
                title={bg
                  ? `Имаш ${streak.freezes_remaining} ден гратис тази седмица — ако пропуснеш ден, streak-ът няма да се чупи.`
                  : `${streak.freezes_remaining} grace day(s) this week — your streak survives one miss.`}
              >
                🧊 {streak.freezes_remaining}
              </span>
            )}
            {/* iter582 — 30-day Pro reward badge */}
            {proReward?.granted ? (
              <span
                data-testid="cc-daily-focus-pro-granted"
                className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  isDark ? "bg-violet-500/30 text-violet-100" : "bg-violet-200 text-violet-900"
                }`}
                title={proReward.unlock_until ? (bg ? `Pro до ${String(proReward.unlock_until).slice(0,10)}` : `Pro until ${String(proReward.unlock_until).slice(0,10)}`) : "Pro"}
              >
                👑 {bg ? "Pro" : "Pro"}
              </span>
            ) : (typeof proReward?.days_to_pro === "number" && proReward.days_to_pro > 0 && proReward.days_to_pro <= 10) ? (
              <span
                data-testid="cc-daily-focus-pro-progress"
                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                  isDark ? "bg-violet-500/15 text-violet-200" : "bg-violet-100 text-violet-800"
                }`}
                title={bg
                  ? `Още ${proReward.days_to_pro} дни до безплатен Pro акаунт за 30 дни`
                  : `${proReward.days_to_pro} more day(s) until free 30-day Pro`}
              >
                👑 {bg ? `${proReward.days_to_pro}д до Pro` : `${proReward.days_to_pro}d to Pro`}
              </span>
            ) : null}
          </div>
        )}
      </div>
      <p className={`text-xs font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
        {bg ? "Ако направиш само едно нещо днес:" : "If you do only one thing today:"}
      </p>
      <p className={`mt-1 text-base sm:text-lg font-bold leading-snug ${isDark ? "text-white" : "text-[#1A1A2E]"}`} data-testid="cc-daily-focus-action">
        {focus.action}
      </p>

      {/* iter632 — condensed effects + actions in single row */}
      <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1">
        {(focus.effects || []).map((eff, i) => (
          <span key={i} className={`inline-flex items-center gap-1 text-xs ${isDark ? "text-gray-300" : "text-gray-700"}`}>
            <span className={isDark ? "text-emerald-300" : "text-emerald-600"}>✓</span>
            <span>{eff}</span>
          </span>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="cc-daily-focus-done"
          onClick={handleCompleted}
          disabled={completed}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
            completed
              ? (isDark ? "bg-emerald-500/30 text-emerald-100" : "bg-emerald-200 text-emerald-900")
              : (isDark ? "bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30" : "bg-emerald-100 text-emerald-900 hover:bg-emerald-200")
          }`}
        >
          {completed ? `✓ ${bg ? "Записано" : "Logged"}` : `➜ ${bg ? "Изпълних го" : "I did it"}`}
        </button>
        <Link
          to={focus.cta_path}
          data-testid="cc-daily-focus-learn"
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold transition-colors ${
            isDark ? "text-gray-300 hover:text-white" : "text-gray-700 hover:text-[#1A1A2E]"
          }`}
        >
          {focus.cta_label} →
        </Link>
        <button
          type="button"
          onClick={() => setShowWhy(s => !s)}
          data-testid="cc-daily-focus-why-toggle"
          className={`ml-auto inline-flex items-center gap-1 text-[11px] font-semibold underline decoration-dotted underline-offset-2 ${isDark ? "text-emerald-300 hover:text-emerald-200" : "text-emerald-700 hover:text-emerald-900"}`}
        >
          {showWhy ? "▾" : "▸"} {bg ? "Защо?" : "Why?"}
        </button>
      </div>

      {showWhy && (
        <div
          data-testid="cc-daily-focus-why"
          className={`mt-2 p-2.5 rounded-lg text-xs ${isDark ? "bg-white/[0.03] text-gray-300" : "bg-white/70 text-gray-700"}`}
        >
          <ul className="space-y-0.5">
            {(focus.reasons || []).map((r, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className={isDark ? "text-emerald-300" : "text-emerald-600"}>✓</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* iter582 — 30-day Streak → Free Pro banner (motivational + transparent) */}
      {(() => {
        if (proReward?.granted) {
          const until = proReward.unlock_until ? String(proReward.unlock_until).slice(0, 10) : null;
          return (
            <div
              data-testid="cc-pro-reward-granted"
              className={`mt-4 p-3 rounded-xl border text-xs flex items-start gap-2 ${
                isDark
                  ? "bg-violet-500/10 border-violet-500/30 text-violet-100"
                  : "bg-violet-50 border-violet-200 text-violet-900"
              }`}
            >
              <span className="text-base" aria-hidden="true">👑</span>
              <div>
                <strong>{bg ? "Pro акаунт активиран!" : "Pro account active!"}</strong>{" "}
                {bg
                  ? `Поздравления за 30-дневния streak. Pro е активен ${until ? `до ${until}` : "за 30 дни"}.`
                  : `Congrats on the 30-day streak. Pro is active ${until ? `until ${until}` : "for 30 days"}.`}
              </div>
            </div>
          );
        }
        // iter619 — Derive dtp from streak.current (single source of truth) so the
        // chip "5 поредни" and the progress "5 / 30" never desync.
        const currentStreak = streak?.current || 0;
        const dtp = Math.max(0, 30 - currentStreak);
        if (dtp > 0 && dtp <= 30 && currentStreak > 0) {
          const pct = Math.round((currentStreak / 30) * 100);
          return (
            <div
              data-testid="cc-pro-reward-progress"
              className={`mt-4 p-3 rounded-xl border ${
                isDark ? "bg-white/[0.03] border-white/10" : "bg-white/70 border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className={`font-semibold ${isDark ? "text-violet-200" : "text-violet-800"}`}>
                  👑 {bg ? "30 дни поредни = безплатен Pro" : "30-day streak = free Pro"}
                </span>
                <span className={`font-bold tabular-nums ${isDark ? "text-violet-300" : "text-violet-700"}`} data-testid="cc-pro-reward-progress-num">
                  {currentStreak} / 30
                </span>
              </div>
              <div className={`w-full h-1.5 rounded-full overflow-hidden ${isDark ? "bg-white/5" : "bg-gray-100"}`}>
                <div className="h-full bg-violet-500 transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
              <p className={`mt-2 text-[11px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {bg
                  ? `Още ${dtp} ${dtp === 1 ? "ден" : "дни"} до автоматично отключване на 30 дни безплатен Pro.`
                  : `${dtp} more day(s) to auto-unlock 30 days of free Pro.`}
              </p>
            </div>
          );
        }
        return null;
      })()}
    </section>
  );
}
